import { useMemo, useCallback, useEffect, useRef, useState } from "react";
import type {
  MouseEvent as ReactMouseEvent,
  ReactNode,
  SyntheticEvent,
} from "react";
import { observer } from "mobx-react-lite";
import { Input, Popover } from "antd";
import { CheckOutlined, DeleteOutlined } from "@ant-design/icons";
import { useMst } from "../../stores/main";
import {
  clamp,
  frameToPixel,
  pixelToFrame,
  getTickInterval,
  frameToSimpleTime,
} from "./utils/timelineHelpers";

interface Marker {
  index: number;
  cm: string;
  tm: number;
  dr?: number;
}

interface MarkerPatch {
  name?: string;
  time?: number;
  duration?: number | null;
}

interface TimelineRulerProps {
  totalFrames: number;
  frameRate: number;
  compositionStart: number;
  onSeek: (frame: number) => void;
  markers?: Marker[];
  onMarkerUpdate?: (markerIndex: number, patch: MarkerPatch) => void;
  onMarkerDelete?: (markerIndex: number) => void;
  children?: ReactNode;
}

const MARKER_DRAG_THRESHOLD_PX = 3;

const getMarkerDisplayName = (marker: Marker) =>
  marker.cm.trim() || "Marker";

const getMarkerFrame = (marker: Marker, compositionStart: number) =>
  Number.isFinite(marker.tm) ? marker.tm : compositionStart;

const TimelineMarkerControl = ({
  marker,
  compositionStart,
  totalFrames,
  zoom,
  scrollOffset,
  onMarkerUpdate,
  onMarkerDelete,
  onSeek,
}: {
  marker: Marker;
  compositionStart: number;
  totalFrames: number;
  zoom: number;
  scrollOffset: number;
  onMarkerUpdate?: (markerIndex: number, patch: MarkerPatch) => void;
  onMarkerDelete?: (markerIndex: number) => void;
  onSeek: (frame: number) => void;
}) => {
  const markerName = getMarkerDisplayName(marker);
  const markerFrame = getMarkerFrame(marker, compositionStart);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(markerName);
  const [time, setTime] = useState(String(Math.round(markerFrame)));
  const [dragFrame, setDragFrame] = useState<number | null>(null);
  const cleanupDragRef = useRef<(() => void) | null>(null);
  const suppressNextOpenRef = useRef(false);
  const compositionEnd = compositionStart + totalFrames;
  const displayFrame = dragFrame ?? markerFrame;
  const markerX = frameToPixel(
    displayFrame - compositionStart,
    zoom,
    scrollOffset
  );

  const resetForm = useCallback(() => {
    setName(markerName);
    setTime(String(Math.round(markerFrame)));
  }, [markerFrame, markerName]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (suppressNextOpenRef.current && nextOpen) {
        suppressNextOpenRef.current = false;
        return;
      }

      if (nextOpen) {
        resetForm();
      }
      setOpen(nextOpen);
    },
    [resetForm]
  );

  const stopTimelineEvent = useCallback((event: SyntheticEvent) => {
    event.stopPropagation();
  }, []);

  useEffect(() => {
    return () => {
      cleanupDragRef.current?.();
    };
  }, []);

  const parsedTime = Number(time);
  const canSave = Number.isFinite(parsedTime);

  const handleSave = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (!canSave) {
        return;
      }

      onMarkerUpdate?.(marker.index, {
        name,
        time: clamp(Math.round(parsedTime), compositionStart, compositionEnd),
      });
      setOpen(false);
    },
    [
      canSave,
      compositionEnd,
      compositionStart,
      marker.index,
      name,
      onMarkerUpdate,
      parsedTime,
    ]
  );

  const handleDelete = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onMarkerDelete?.(marker.index);
      setOpen(false);
    },
    [marker.index, onMarkerDelete]
  );

  const handleMarkerMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (event.button !== 0 || !onMarkerUpdate) {
        return;
      }

      event.preventDefault();

      const ownerDocument = event.currentTarget.ownerDocument;
      const safeZoom = Math.max(zoom, 0.001);
      const dragState = {
        startClientX: event.clientX,
        startFrame: markerFrame,
        nextFrame: markerFrame,
        moved: false,
      };

      cleanupDragRef.current?.();

      const getFrameForClientX = (clientX: number) =>
        clamp(
          Math.round(
            dragState.startFrame + (clientX - dragState.startClientX) / safeZoom
          ),
          compositionStart,
          compositionEnd
        );

      const cleanupDrag = () => {
        ownerDocument.removeEventListener("mousemove", handleMouseMove);
        ownerDocument.removeEventListener("mouseup", handleMouseUp);
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        moveEvent.preventDefault();

        if (
          !dragState.moved &&
          Math.abs(moveEvent.clientX - dragState.startClientX) >=
            MARKER_DRAG_THRESHOLD_PX
        ) {
          dragState.moved = true;
          setOpen(false);
        }

        if (!dragState.moved) {
          return;
        }

        dragState.nextFrame = getFrameForClientX(moveEvent.clientX);
        setDragFrame(dragState.nextFrame);
      };

      const handleMouseUp = (upEvent: MouseEvent) => {
        cleanupDrag();
        cleanupDragRef.current = null;
        setDragFrame(null);

        if (!dragState.moved) {
          return;
        }

        upEvent.preventDefault();
        upEvent.stopPropagation();
        suppressNextOpenRef.current = true;

        if (dragState.nextFrame !== dragState.startFrame) {
          onMarkerUpdate(marker.index, { time: dragState.nextFrame });
        }

        window.setTimeout(() => {
          suppressNextOpenRef.current = false;
        }, 0);
      };

      cleanupDragRef.current = cleanupDrag;
      ownerDocument.addEventListener("mousemove", handleMouseMove);
      ownerDocument.addEventListener("mouseup", handleMouseUp);
    },
    [
      compositionEnd,
      compositionStart,
      marker.index,
      markerFrame,
      onMarkerUpdate,
      zoom,
    ]
  );

  const handleMarkerClick = useCallback((event: SyntheticEvent) => {
    event.stopPropagation();
  }, []);

  const handleMarkerDoubleClick = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      onSeek(markerFrame);
    },
    [markerFrame, onSeek]
  );

  if (markerX < -50 || markerX > 2000) {
    return null;
  }

  const content = (
    <form
      className="timeline-marker-popover"
      onClick={stopTimelineEvent}
      onKeyDown={stopTimelineEvent}
      onKeyUp={stopTimelineEvent}
      onMouseDown={stopTimelineEvent}
      onMouseUp={stopTimelineEvent}
      onSubmit={handleSave}
    >
      <label className="timeline-marker-popover__field">
        <span>Name</span>
        <Input
          size="small"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onClick={stopTimelineEvent}
        />
      </label>
      <label className="timeline-marker-popover__field">
        <span>Frame</span>
        <Input
          size="small"
          type="number"
          min={compositionStart}
          max={compositionEnd}
          value={time}
          onChange={(event) => setTime(event.target.value)}
          onClick={stopTimelineEvent}
        />
      </label>
      <div className="timeline-marker-popover__actions">
        <button
          type="submit"
          className="timeline-marker-popover__action"
          disabled={!canSave}
          title="Save marker"
          aria-label="Save marker"
        >
          <CheckOutlined />
        </button>
        <button
          type="button"
          className="timeline-marker-popover__action timeline-marker-popover__action--danger"
          onClick={handleDelete}
          title="Delete marker"
          aria-label="Delete marker"
        >
          <DeleteOutlined />
        </button>
      </div>
    </form>
  );

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      content={content}
      title={markerName}
      trigger="click"
      placement="top"
    >
      <button
        type="button"
        className={`timeline-ruler__marker ${
          dragFrame != null ? "timeline-ruler__marker--dragging" : ""
        }`}
        style={{ left: markerX }}
        onMouseDown={handleMarkerMouseDown}
        onClick={handleMarkerClick}
        onDoubleClick={handleMarkerDoubleClick}
        title={markerName}
        aria-label={`Edit marker ${markerName}`}
      />
    </Popover>
  );
};

export const TimelineRuler = observer(
  ({
    totalFrames,
    frameRate,
    compositionStart,
    onSeek,
    markers,
    onMarkerUpdate,
    onMarkerDelete,
    children,
  }: TimelineRulerProps) => {
    const { timelineStore } = useMst();
    const { zoom, scrollOffset } = timelineStore;

    const handleMouseDown = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
      },
      []
    );

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const frame = pixelToFrame(x, zoom, scrollOffset) + compositionStart;
        onSeek(
          Math.max(
            compositionStart,
            Math.min(compositionStart + totalFrames, frame)
          )
        );
      },
      [zoom, scrollOffset, compositionStart, totalFrames, onSeek]
    );

    const ticks = useMemo(() => {
      const { major, minor } = getTickInterval(zoom, frameRate);
      const result: Array<{
        frame: number;
        isMajor: boolean;
        label?: string;
      }> = [];

      // Calculate visible range
      const startFrame = Math.max(0, pixelToFrame(0, zoom, scrollOffset));
      const endFrame = pixelToFrame(2000, zoom, scrollOffset); // Assume max 2000px width

      // Generate ticks
      for (let frame = 0; frame <= totalFrames; frame += minor) {
        if (frame < startFrame - minor || frame > endFrame + minor) continue;

        const isMajor = frame % major === 0;
        result.push({
          frame,
          isMajor,
          label: isMajor ? frameToSimpleTime(frame, frameRate) : undefined,
        });
      }

      return result;
    }, [zoom, scrollOffset, totalFrames, frameRate]);

    return (
      <div
        className="timeline-ruler"
        onMouseDown={handleMouseDown}
        onClick={handleClick}
      >
        {ticks.map(({ frame, isMajor, label }) => {
          const x = frameToPixel(frame, zoom, scrollOffset);
          if (x < -50 || x > 2000) return null;

          const firstFrame = frame === 0;
          const formattedLabel = firstFrame ? "0s" : label;
          const labelPosition = firstFrame ? 10 : x;

          return (
            <div key={frame}>
              <div
                className={`timeline-ruler__tick ${
                  isMajor
                    ? "timeline-ruler__tick--major"
                    : "timeline-ruler__tick--minor"
                }`}
                style={{ left: x }}
                draggable={false}
              />
              {formattedLabel && (
                <span
                  className="timeline-ruler__label"
                  style={{ left: labelPosition }}
                  draggable={false}
                >
                  {formattedLabel}
                </span>
              )}
            </div>
          );
        })}
        {markers?.map((marker, index) => {
          return (
            <TimelineMarkerControl
              key={`marker-${marker.index}-${index}-${marker.tm}`}
              marker={marker}
              compositionStart={compositionStart}
              totalFrames={totalFrames}
              zoom={zoom}
              scrollOffset={scrollOffset}
              onMarkerUpdate={onMarkerUpdate}
              onMarkerDelete={onMarkerDelete}
              onSeek={onSeek}
            />
          );
        })}
        {children}
      </div>
    );
  }
);
